import moment from "moment";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DailyWorkExecutionTask } from "@/models/daily-work.model";
import { CheckCircle2 } from "lucide-react";

interface TaskChecklistProps {
  tasks: DailyWorkExecutionTask[];
  onTaskToggle: (position: number, done: boolean) => void;
  readOnly?: boolean;
}

export function TaskChecklist({ tasks, onTaskToggle, readOnly = false }: TaskChecklistProps) {
  const getOverallProgress = () => {
    const completed = tasks.filter((task) => task.done).length;
    const total = tasks.length;
    return { completed, total };
  };

  const overallProgress = getOverallProgress();

  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No tasks defined for this daily work.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Overall Progress */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Overall Progress</h3>
          <Badge variant="secondary">
            {overallProgress.completed} of {overallProgress.total} tasks completed
          </Badge>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{
              width: overallProgress.total > 0 ? `${(overallProgress.completed / overallProgress.total) * 100}%` : "0%",
            }}
          />
        </div>
      </div>

      {/* Task List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tasks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {tasks.map((task, index) => (
            <div key={index} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={task.done}
                onCheckedChange={(checked) => onTaskToggle(index, checked as boolean)}
                disabled={readOnly}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${task.done ? "line-through text-muted-foreground" : ""}`}>
                    {task.name}
                  </span>
                  {task.done && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                </div>
                {task.completed_at && (
                  <span className="text-xs text-muted-foreground">
                    Completed at {moment(task.completed_at).format("MM/DD/YYYY h:mm A")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

